import { mdiMagnify } from './icons';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import type { Link } from '../api';
import { enterAction, matchLinks, parseQuery, LOCAL_PREFIX } from '../search/search';
import { settings } from '../state';
import { LinkIconView, Svg } from './Icon';

export function openUrl(url: string, newTab: boolean) {
  if (newTab) window.open(url, '_blank', 'noopener,noreferrer');
  else window.location.href = url;
}

export function linkOpensInNewTab(link: Link): boolean {
  return link.openIn === 'default'
    ? settings.value.links.openIn === 'new_tab'
    : link.openIn === 'new_tab';
}

export function SearchBar({ links }: { links: Link[] }) {
  const [input, setInput] = useState('');
  const [selected, setSelected] = useState(0);
  const ref = useRef<HTMLInputElement>(null);

  const { query, provider } = parseQuery(input, settings.value);
  const searchLocal = !provider || provider.prefix === LOCAL_PREFIX;
  const matches = useMemo(
    () => (searchLocal ? matchLinks(links, query) : []),
    [links, query, searchLocal]
  );
  const action = enterAction(input, settings.value, matches, selected);

  // "/" focuses search from anywhere on the page.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      if (e.key !== '/' || e.ctrlKey || e.metaKey) return;
      if (target.closest('input, textarea, select, [contenteditable], dialog')) return;
      e.preventDefault();
      ref.current?.focus();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  useEffect(() => setSelected(0), [input]);

  function onKeyDown(e: KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelected((i) => Math.min(i + 1, Math.max(matches.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelected((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Escape') {
      if (input) setInput('');
      else ref.current?.blur();
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const newTab = e.ctrlKey || e.metaKey;
      if (action.kind === 'link') {
        openUrl(action.link.url, newTab || linkOpensInNewTab(action.link));
      } else if (action.kind === 'open') {
        openUrl(action.url, newTab || settings.value.links.openIn === 'new_tab');
      }
    }
  }

  const showPanel = input.trim().length > 0;
  return (
    <div class="search">
      <div class="search-box">
        <Svg path={mdiMagnify} />
        <input
          ref={ref}
          type="search"
          value={input}
          onInput={(e) => setInput(e.currentTarget.value)}
          onKeyDown={onKeyDown}
          placeholder="Search links, or /g for the web"
          aria-label="Search"
          aria-expanded={showPanel}
          aria-controls="search-results"
          autoComplete="off"
          autoFocus={!matchMedia('(pointer: coarse)').matches}
        />
        <kbd class="search-hint">/</kbd>
      </div>
      {showPanel && (
        <ul id="search-results" class="search-results" role="listbox">
          {matches.map((link, i) => (
            <li key={link.id} role="option" aria-selected={i === selected}>
              <a
                href={link.url}
                class={i === selected ? 'active' : ''}
                target={linkOpensInNewTab(link) ? '_blank' : undefined}
                rel="noopener noreferrer"
                onMouseEnter={() => setSelected(i)}
              >
                <LinkIconView icon={link.icon} title={link.title} size={18} />
                <span class="search-title">{link.title}</span>
                <span class="search-url">{link.url.replace(/^https?:\/\//, '')}</span>
              </a>
            </li>
          ))}
          {action.kind === 'open' && (
            <li class="search-action">
              <span>
                ↵ {action.label}
                {matches.length === 0 ? '' : ' (no match selected)'}
              </span>
            </li>
          )}
          {matches.length === 0 && action.kind === 'none' && (
            <li class="search-action">No matching links.</li>
          )}
        </ul>
      )}
    </div>
  );
}
